import { Formik, useField } from 'formik';
import { memo } from 'react';
import * as Yup from 'yup';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type Board } from '@air/api/types';
import { FormikField } from '@air/formik-field';
import { Button } from '@air/primitive-button';
import { Input } from '@air/primitive-input';
import { Modal, ModalCloseButton, ModalTitle } from '@air/primitive-modal';
import { Spinner } from '@air/primitive-spinner';
import { Textarea } from '@air/primitive-textarea';
import { convertUnknownToError } from '@air/utils-error';

import { BoardId } from '~/components/EditPrivateBoardModal/EditBoardModal/BoardId/BoardId';
import { SlackIntegration } from '~/components/EditPrivateBoardModal/EditBoardModal/SlackIntegration/SlackIntegration';
import Form from '~/components/Form';
import { EDIT_BOARD_INPUT } from '~/constants/testIDs';
import { useShowSubscriptionExpiredModal } from '~/hooks/useShowSubscriptionExpiredModal';
import { useUpdateBoard } from '~/swr-hooks/boards/useUpdateBoard';
import { useBoardSlackIntegrations } from '~/swr-hooks/slack/useBoardSlackIntegrations';
import { useUpdateBoardSlackIntegration } from '~/swr-hooks/slack/useUpdateBoardSlackIntegration';
import { containsRestrictedPathChars } from '~/utils/FileUtils';
import { ITEM_NAME_MAX_LENGTH } from '~/utils/validationSchemas';

export type EditBoardModalProps = {
  board: Board;
  trackLocation: AirActionTrackingLocation;
};

const EditBoardSchema = Yup.object({
  title: Yup.string()
    .trim()
    .max(ITEM_NAME_MAX_LENGTH, `Cannot be longer than ${ITEM_NAME_MAX_LENGTH} characters`)
    .test(
      'restricted-char-validation',
      'Characters : and | cannot be used',
      (val) => !containsRestrictedPathChars(val || ''),
    )
    .required('This field is required'),
  description: Yup.string(),
});

const DescriptionTextArea = memo(() => {
  const [field] = useField('description');

  return <Textarea size="large" {...field} />;
});
DescriptionTextArea.displayName = 'DescriptionTextArea';

export const EditBoardModal = memo(({ board, onClose, trackLocation }: AirModalProps<EditBoardModalProps>) => {
  const { description, title } = board;

  const { data: slackIntegrationData, isLoading: isLoadingSlackIntegration } = useBoardSlackIntegrations(board.id);

  const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({ onClose });
  const { updateBoard } = useUpdateBoard();
  const { isPending: isUpdatingSlackIntegration, mutate: updateBoardSlackIntegration } =
    useUpdateBoardSlackIntegration();

  if (showingSubscriptionExpiredModal) {
    return null;
  }

  return (
    <Formik
      validationSchema={EditBoardSchema}
      isLoading={isUpdatingSlackIntegration}
      // biome-ignore lint/performance/noJsxPropsBind: submit writes this board's title and description and maps the mutation error onto the form
      onSubmit={async ({ title, description }, { setErrors }) =>
        updateBoard.mutate(
          { board, values: { title, description }, trackLocation },
          {
            onError: (error) => {
              const _error = convertUnknownToError(error);

              setErrors({ title: _error.message });
            },
            onSuccess: () => {
              onClose();
            },
          },
        )
      }
      initialValues={{ title, description: description || '' }}
      // biome-ignore lint/performance/noJsxPropsBind: the Formik render prop closes over this modal's slack and board state
      render={(FormikProps) => (
        <Modal
          className="flex flex-col gap-4"
          dangerouslyBypassFocusLock
          dangerouslyBypassScrollLock
          data-testid="EDIT_BOARD_DETAILS_MODAL"
          isOpen
          onDismiss={onClose}
        >
          <header className="flex justify-between gap-2">
            <ModalTitle>Edit details</ModalTitle>
            <ModalCloseButton onClick={onClose} />
          </header>

          <Form className="flex flex-col gap-6" id="edit-board" data-testid={EDIT_BOARD_INPUT}>
            <FormikField
              component={<Input autoFocus size="large" data-testid="EDIT_BOARD_TITLE_INPUT" />}
              id="title"
              label="Board name"
              name="title"
            />

            <FormikField
              component={<Textarea data-testid="EDIT_BOARD_DESCRIPTION_TEXTAREA" />}
              id="description"
              label="Description (Optional)"
              name="description"
            />
          </Form>

          {isUpdatingSlackIntegration || isLoadingSlackIntegration ? (
            <div className="flex items-center justify-center py-3">
              <Spinner className="size-8 ads:text-ads-cyan-10 text-teal-9" />
            </div>
          ) : (
            !!slackIntegrationData && (
              <SlackIntegration
                integration={slackIntegrationData[0]}
                board={board}
                updateBoardSlackIntegration={updateBoardSlackIntegration}
              />
            )
          )}

          {!!board.id && <BoardId id={board.id} />}
          <footer className="flex items-center justify-end gap-2">
            <Button appearance="ghost" color="gray" onClick={onClose} size="large">
              Cancel
            </Button>
            <Button
              appearance="filled"
              color="blue"
              data-testid="EDIT_BOARD_MODAL_SAVE_BUTTON"
              disabled={!FormikProps.isValid}
              form="edit-board"
              isLoading={updateBoard.isPending}
              size="large"
              type="submit"
            >
              Save
            </Button>
          </footer>
        </Modal>
      )}
    />
  );
});

EditBoardModal.displayName = 'EditBoardModal';
